import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Package, UsersThree, ArrowsClockwise, Info } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const ResourceSummary = () => {
  const { projectId } = useParams();
  const [data, setData] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('materials');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [sumRes, sugRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/projects/${projectId}/resource-summary`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/projects/${projectId}/resource-suggestion`, { credentials: 'include' }),
      ]);
      if (sumRes.ok) setData(await sumRes.json());
      if (sugRes.ok) {
        const s = await sugRes.json();
        setSuggestions(s.suggestions || []);
      }
    } catch (e) { /* silent */ }
    setLoading(false);
  }, [projectId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <ArrowsClockwise size={24} className="animate-spin text-zinc-400" />
      </div>
    );
  }

  if (!data || (data.total_material_items === 0 && data.total_manpower_roles === 0)) {
    return (
      <div className="text-center py-16" data-testid="resource-empty">
        <Package size={40} className="mx-auto text-zinc-300 mb-3" />
        <p className="text-sm font-medium text-zinc-500">No resource data yet</p>
        <p className="text-xs text-zinc-400 mt-1">
          Add material and manpower recipes to line items in your BOQ to see resource requirements here
        </p>
      </div>
    );
  }

  const materials = data.material_summary || [];
  const manpower = data.manpower_summary || [];

  return (
    <div className="space-y-4" data-testid="resource-summary">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-bold text-zinc-800">Resource Summary</h2>
        <button
          onClick={fetchData}
          className="flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-700"
          data-testid="resource-refresh"
        >
          <ArrowsClockwise size={12} /> Refresh
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-zinc-100 rounded-md p-0.5 w-fit">
        <button
          onClick={() => setTab('materials')}
          className={`px-3 py-1.5 text-xs font-medium rounded ${tab === 'materials' ? 'bg-white text-zinc-800 shadow-sm' : 'text-zinc-500'}`}
          data-testid="tab-materials"
        >
          <Package size={13} className="inline mr-1 -mt-0.5" />
          Materials ({materials.length})
        </button>
        <button
          onClick={() => setTab('manpower')}
          className={`px-3 py-1.5 text-xs font-medium rounded ${tab === 'manpower' ? 'bg-white text-zinc-800 shadow-sm' : 'text-zinc-500'}`}
          data-testid="tab-manpower"
        >
          <UsersThree size={13} className="inline mr-1 -mt-0.5" />
          Manpower ({manpower.length})
        </button>
        <button
          onClick={() => setTab('procurement')}
          className={`px-3 py-1.5 text-xs font-medium rounded ${tab === 'procurement' ? 'bg-white text-zinc-800 shadow-sm' : 'text-zinc-500'}`}
          data-testid="tab-procurement"
        >
          Procurement Suggestion ({suggestions.length})
        </button>
      </div>

      {/* Material Summary */}
      {tab === 'materials' && (
        <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden" data-testid="material-summary-table">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-zinc-50 border-b border-zinc-200">
                <th className="px-4 py-2.5 text-[10px] font-bold text-zinc-500 uppercase">#</th>
                <th className="px-4 py-2.5 text-[10px] font-bold text-zinc-500 uppercase">Material</th>
                <th className="px-4 py-2.5 text-[10px] font-bold text-zinc-500 uppercase text-right">Total Required</th>
                <th className="px-4 py-2.5 text-[10px] font-bold text-zinc-500 uppercase">Unit</th>
              </tr>
            </thead>
            <tbody>
              {materials.map((m, i) => (
                <tr key={i} className="border-b border-zinc-100 last:border-b-0">
                  <td className="px-4 py-2.5 text-xs text-zinc-400">{i + 1}</td>
                  <td className="px-4 py-2.5 text-xs font-medium text-zinc-700">{m.material_name}</td>
                  <td className="px-4 py-2.5 text-xs font-bold text-zinc-800 text-right">{m.total_required.toLocaleString('en-IN')}</td>
                  <td className="px-4 py-2.5 text-xs text-zinc-500">{m.unit}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Manpower Summary */}
      {tab === 'manpower' && (
        <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden" data-testid="manpower-summary-table">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-zinc-50 border-b border-zinc-200">
                <th className="px-4 py-2.5 text-[10px] font-bold text-zinc-500 uppercase">#</th>
                <th className="px-4 py-2.5 text-[10px] font-bold text-zinc-500 uppercase">Type</th>
                <th className="px-4 py-2.5 text-[10px] font-bold text-zinc-500 uppercase">Role</th>
                <th className="px-4 py-2.5 text-[10px] font-bold text-zinc-500 uppercase text-right">Total Man-Days</th>
              </tr>
            </thead>
            <tbody>
              {manpower.map((mp, i) => (
                <tr key={i} className="border-b border-zinc-100 last:border-b-0">
                  <td className="px-4 py-2.5 text-xs text-zinc-400">{i + 1}</td>
                  <td className="px-4 py-2.5">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                      mp.labour_type === 'Skilled' ? 'bg-blue-50 text-blue-700' :
                      mp.labour_type === 'Supervisor' ? 'bg-purple-50 text-purple-700' :
                      'bg-zinc-100 text-zinc-600'
                    }`}>{mp.labour_type}</span>
                  </td>
                  <td className="px-4 py-2.5 text-xs font-medium text-zinc-700">{mp.role_name || '-'}</td>
                  <td className="px-4 py-2.5 text-xs font-bold text-zinc-800 text-right">{mp.total_manpower.toLocaleString('en-IN')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Procurement Suggestion */}
      {tab === 'procurement' && (
        <div data-testid="procurement-suggestion-table">
          <div className="flex items-center gap-2 mb-2">
            <Info size={14} className="text-blue-500" />
            <span className="text-[10px] text-zinc-500">Suggested quantities based on BOQ requirements minus already-indented quantities. Use these as reference when raising indents.</span>
          </div>
          <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-zinc-50 border-b border-zinc-200">
                  <th className="px-4 py-2.5 text-[10px] font-bold text-zinc-500 uppercase">Material</th>
                  <th className="px-4 py-2.5 text-[10px] font-bold text-zinc-500 uppercase text-right">BOQ Needs</th>
                  <th className="px-4 py-2.5 text-[10px] font-bold text-zinc-500 uppercase text-right">Already Indented</th>
                  <th className="px-4 py-2.5 text-[10px] font-bold text-zinc-500 uppercase text-right">Suggested</th>
                  <th className="px-4 py-2.5 text-[10px] font-bold text-zinc-500 uppercase">Unit</th>
                </tr>
              </thead>
              <tbody>
                {suggestions.map((s, i) => (
                  <tr key={i} className="border-b border-zinc-100 last:border-b-0">
                    <td className="px-4 py-2.5 text-xs font-medium text-zinc-700">{s.material_name}</td>
                    <td className="px-4 py-2.5 text-xs text-zinc-600 text-right">{s.boq_required.toLocaleString('en-IN')}</td>
                    <td className="px-4 py-2.5 text-xs text-zinc-400 text-right">{s.already_indented.toLocaleString('en-IN')}</td>
                    <td className={`px-4 py-2.5 text-xs font-bold text-right ${s.suggested_quantity > 0 ? 'text-amber-700' : 'text-green-600'}`}>
                      {s.suggested_quantity.toLocaleString('en-IN')}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-zinc-500">{s.unit}</td>
                  </tr>
                ))}
                {suggestions.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-xs text-zinc-400">No materials with resource recipes in BOQ</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
